import Dropdown from 'admin/components/shared/Dropdown'
import ShowField from 'admin/components/shared/ShowField'
import Tags from 'admin/components/shared/Tags'
import useOrganisation from 'admin/hooks/useOrganisation'
import useSchedule from 'admin/hooks/useSchedule'
import useServices from 'admin/hooks/useServices'
import {formatDate, formatNumber, frequencyInWords, pluralise} from 'admin/lib/format'
import moment from 'admin/lib/moment'
import invert from 'invert-color'
import {isEmpty} from 'lodash'
import React, {Fragment} from 'react'
import {Link, useParams} from 'react-router-dom'
import {daysToFrequency} from '../../lib/format'
import DelimitedList from '../shared/DelimitedList'
import Alerts from '../Alerts'
import ModalLink from '../shared/ModalLink'

const Icon = ({name, color = 'text', hidden = false}) => {
  if (hidden) return null
  return (
    <span className={`icon has-text-${color}`}>
      <i className={`fa fa-sm fa-${name}`}></i>
    </span>
  )
}

const PlanTag = ({plan}) => {
  let style = {}
  let color = plan.color || '#F5F5F5'
  style.backgroundColor = `${color}`
  style.color = invert(`${color}`, true)

  return (
    <span className='tag is-small' style={style}>
      {plan.name}
    </span>
  )
}

export default ({account, breadcrumbs, onEdit, onArchive, onRecalculate}) => {
  const {organisationID, accountID} = useParams()
  const {schedule} = useSchedule(organisationID, accountID)
  const {services} = useServices(organisationID, accountID, undefined, {includeFilters: false})
  const {organisation} = useOrganisation(organisationID)

  if (isEmpty(account)) return null

  return (
    <Fragment>
      <div className='columns is-vcentered is-mobile'>
        <div className='column'>{breadcrumbs}</div>

        <div className='column buttons is-right'>
          <ModalLink modal='history' params={{itemType: 'Account', itemId: accountID}} className='button is-light'>
            <span className='icon is-small'>
              <i className='fa fa-history' aria-hidden='true'></i>
            </span>
            <span>View History & Notes</span>
          </ModalLink>

          {account.state === 'active' || !!account.xeroLink ? (
            <Dropdown id='account-options-menu' className='is-right'>
              <button className='button is-light'>
                <span>Actions</span>
                <span className='icon'>
                  <i className='fa fa-caret-down' aria-hidden='true'></i>
                </span>
              </button>
              {account.state === 'active' ? (
                <Fragment>
                  {organisation.smsEnabled ? (
                    <ModalLink modal='new-text-message' params={{accountIds: accountID}} className='dropdown-item'>
                      Text message
                    </ModalLink>
                  ) : null}
                  <a onClick={onArchive} className='dropdown-item'>
                    Archive
                  </a>
                </Fragment>
              ) : null}
              {account.xeroLink && (
                <a className='dropdown-item' href={account.xeroLink} target='_blank'>
                  <span>Show in Xero</span>
                  <span className='icon'>
                    <i className='fa fa-external-link'></i>
                  </span>
                </a>
              )}
            </Dropdown>
          ) : null}
          <button onClick={onEdit} className='button is-light'>
            Edit
          </button>
        </div>
      </div>

      <div className='columns is-variable is-7-desktop'>
        <div className='column is-flex is-flex-direction-column is-justify-content-space-between'>
          <div>
            <ShowField
              className='mb-1'
              label={<Icon name='hashtag' />}
              alwaysHorizontal={true}
              flexGrow={false}
              type='text'
              showNone>
              {account.customerNumber}
            </ShowField>
            <ShowField
              className='mb-1'
              label={<Icon name='user' />}
              alwaysHorizontal={true}
              flexGrow={false}
              values={{contactFirstName: account.contactFirstName, contactLastName: account.contactLastName}}
            />
            <ShowField
              value={account.contactAddress}
              className='mb-1'
              label={<Icon name='envelope' />}
              alwaysHorizontal={true}
              flexGrow={false}
              type='address'
            />
            <ShowField
              value={account.contactPhone}
              className='mb-1'
              label={<Icon name='phone' />}
              alwaysHorizontal={true}
              flexGrow={false}
              type='tel'
            />
            <ShowField
              value={account.contactEmail}
              className='mb-1'
              label={<Icon name='at' />}
              alwaysHorizontal={true}
              flexGrow={false}
              type='email'
            />
            <ShowField
              value={account.billingEmail}
              className='mb-1'
              label={<Icon name='at' />}
              alwaysHorizontal={true}
              flexGrow={false}
              type='email'
            />
          </div>
          <div>
            <ShowField
              value={daysToFrequency(account.paymentFrequencyInDays)}
              className='is-size-7 mb-0'
              label='Avg payment freq'
            />
            <ShowField
              value={moment(account.createdAt).fromNow(true) + ' ago'}
              className='is-size-7 mb-0'
              label='Account created'
            />

            {organisation.featureFlags?.includes('referral_codes') ? (
              <Fragment>
                <div className='field is-horizontal is-size-7 mb-0'>
                  <div className='field-label is-small has-text-left is-flex-grow-2'>
                    <label className='label is-small'>Referral code</label>
                  </div>
                  <div className='field-body'>
                    <div style={{wordBreak: 'break-word', whiteSpace: 'pre-wrap', width: '100%'}}>
                      <Fragment>
                        {account.referralCode}{' '}
                        {account.referredAccounts.length ? (
                          <Link to={`/orgs/${organisationID}/accounts/active?referredById=${account.id}`}>
                            {pluralise(`referral`, account.referredAccounts.length, true)}
                          </Link>
                        ) : null}
                      </Fragment>
                    </div>
                  </div>
                </div>

                <ShowField className='is-size-7 mb-0' label='Referred by'>
                  {account.referredBy && `${account.referredBy.displayName} (${account.referredByCode})`}
                </ShowField>
              </Fragment>
            ) : null}
          </div>
        </div>

        <div className='column'>
          <ShowField label='Groups' showNone>
            {account.groups?.length && <Tags selected={account.groups} />}
          </ShowField>
          {(!!organisation.smsEnabled || organisation.featureFlags?.includes('running_balance_invoices')) && (
            <ShowField label='Features' showNone>
              {!!organisation.smsEnabled && account.warnWhenOverdue && (
                <div className='is-italic is-size-7'>
                  <Icon name='regular fa-bell' />
                  {'  '}Send SMS warnings when overdue
                </div>
              )}
              {organisation.featureFlags?.includes('running_balance_invoices') && account.useRunningBalanceInvoices && (
                <div className='is-italic is-size-7'>
                  <Icon name='regular fa-file-lines' />
                  {'  '}Use running balance invoices
                </div>
              )}
            </ShowField>
          )}
          <ShowField className='is-size-7' label='Payment detail'>
            {account.paymentDetail?.name}
          </ShowField>
          <ShowField label='Balance'>
            <div className='is-flex is-align-items-baseline'>
              {Number(account.balance) >= 0 ? (
                <div>
                  <span className='is-size-5'>{formatNumber(account.balance, {accounting: true})}</span>
                  {!!Number(account.balance > 0) && <span> credit</span>}
                </div>
              ) : (
                <div>
                  <span className='is-size-5 has-text-danger'>{formatNumber(Math.abs(account.balance))}</span>{' '}
                  <span className='has-text-danger'>owing</span>
                  {account.overdueSince ? (
                    <sub className='has-text-danger'>
                      {-account.overdueAmount != account.balance
                        ? `${formatNumber(account.overdueAmount)} overdue`
                        : 'Overdue'}
                      {` since ${account.overdueSince}`}
                    </sub>
                  ) : null}
                </div>
              )}
              <a onClick={onRecalculate} className='icon mx-2 has-text-info'>
                <i className={`fa fa-sm fa-refresh`}></i>
              </a>
            </div>
          </ShowField>
          {schedule.length ? (
            <ShowField label='Upcoming invoices'>
              <div className='buttons'>
                {schedule.map((invoice, i) => (
                  <Link
                    key={i}
                    className='button is-white is-small'
                    to={`/orgs/${organisationID}/${
                      {
                        Invoice: 'invoices',
                        RepeatingInvoice: 'repeating_invoices'
                      }[invoice.__typename]
                    }/${invoice.id}`}>
                    <DelimitedList>
                      {invoice.__typename === 'RepeatingInvoice'
                        ? formatDate(invoice.nextInvoiceDate)
                        : formatDate(invoice.date)}
                      <span>
                        {formatNumber(invoice.amount)}{' '}
                        {invoice.__typename === 'RepeatingInvoice'
                          ? `every ${frequencyInWords(invoice.frequencyCount, invoice.frequency)}`
                          : 'one off'}
                      </span>
                      {invoice.endDate && <Fragment>Ends {formatDate(invoice.endDate)}</Fragment>}
                      {invoice.state === 'draft' ? (
                        <span className='tag is-small has-background-grey-light'>Draft</span>
                      ) : (
                        invoice.isSendable && <span className='tag is-success is-light is-small'>Send</span>
                      )}
                    </DelimitedList>
                  </Link>
                ))}
              </div>
            </ShowField>
          ) : null}
          {organisation.featureFlags?.includes('internet_services') ? (
            <ShowField label='Services'>
              <div className='buttons'>
                {services.map((service, i) => (
                  <ModalLink
                    key={i}
                    modal='service'
                    params={{serviceId: service.id}}
                    className='button is-white is-small'>
                    <Icon name='solid fa-flag' color='danger' hidden={service.numOfMatches === 1} />
                    <Icon
                      name='regular fa-circle-dot'
                      color={{active: 'success', inactive: 'danger'}[service.status]}
                    />
                    <DelimitedList>
                      {service.plan && <PlanTag plan={service.plan} />}
                      {service.description?.length ? service.description : null}
                      {service.address?.length || service.coords ? (
                        <span>{service.address || service.coords?.join(', ')}</span>
                      ) : null}
                    </DelimitedList>
                  </ModalLink>
                ))}
                <ModalLink modal='new-service' params={{accountId: accountID}} className='button is-light is-small'>
                  Add service
                </ModalLink>
              </div>
            </ShowField>
          ) : null}
        </div>
      </div>

      <Alerts ownerType='Account' ownerId={accountID} />
    </Fragment>
  )
}
